<template>
    <nav v-if="nav.items" class="navbar__menu" :data-testid="`navbar-menu`">
        <ul>
            <li v-for="(menu, i) in nav.items" :key="`menu-item-${i}`">
                <NavbarMenuItem v-if="menu" :menu="menu" :index="i + 1" :is-open="openMenuIndex === i" @click="$emit('toggle-menu', i)" />
            </li>
            <li :class="['navbar__menu-locale-selection', {'navbar__menu-locale-selection--submenu-open': openMenuIndex === null}]">
                <LocaleSelection />
            </li>
        </ul>
    </nav>
</template>

<script setup lang="ts">
    import type {NavbarConfig} from '@navbar-unlogged-types';
    import NavbarMenuItem from './NavbarMenuItem.vue';
    import LocaleSelection from '../features/localization-selection/ui/LocaleSelection.vue';

    defineProps<{
        nav: NavbarConfig;
        openMenuIndex: number | null;
    }>();

    defineEmits<{
        'toggle-menu': [index: number];
    }>();
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* mobile-first */
    .navbar__menu {
        /* Stays static so the submenu is positioned against `.navbar__content` and covers the actions while they fade out. */
        position: static;
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        min-height: 0;
        padding: var(--joy-core-spacing-8) var(--joy-core-spacing-5);
        border-top: 1px solid var(--joy-color-neutral-30);

        > * {
            overscroll-behavior: contain;
        }

        > ul {
            list-style: none;
            flex: 1 1 0;
            min-height: 0;
            overflow-y: auto;
            margin: 0;

            > li {
                &:not(:last-child) {
                    margin-bottom: var(--joy-core-spacing-5);
                }
            }
        }

        &-locale-selection {
            opacity: 0;
            transition: opacity 0.3s ease-in-out;

            &--submenu-open {
                opacity: 1;
            }
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar__menu {
            position: static;
            display: block;
            flex: 0 1 auto;
            height: 100%;
            padding: 0;
            border-top: none;

            > ul {
                list-style-type: none;
                width: 100%;
                display: flex;
                align-items: center;
                flex: none;
                min-height: 0;
                height: 100%;
                overflow: visible;
                padding: 0;
                margin: 0;

                > li {
                    display: block;
                    list-style-type: none;
                    height: 100%;
                    border-bottom: none;

                    &:not(:last-child) {
                        margin-bottom: 0;
                    }
                }
            }

            li.navbar__menu-locale-selection {
                display: none;
            }
        }
    }
</style>
